// Tipos para el sistema de gestión de minutas

import type { Receta } from './recetas';

export interface TipoPlato {
  id: string;
  nombre: string; // 'Normal', 'Dieta Blanda', 'Hipocalórico', etc.
  descripcion?: string;
  color?: string; // color hex para UI
  orden: number; // para ordenar en UI
  activo: boolean;
}

// LEGACY: Tipos de platos predeterminados para casinos
// @deprecated - Usar TiposPlato.obtenerTodos() para carga dinámica
// Mantenido solo para compatibilidad con componentes LEGACY
export const TIPOS_PLATOS_DEFAULT: TipoPlato[] = [
  {
    id: 'normal',
    nombre: 'Normal',
    descripcion: 'Plato principal estándar',
    color: '#3B82F6',
    orden: 1,
    activo: true,
  },
  {
    id: 'dieta-blanda',
    nombre: 'Dieta Blanda',
    descripcion: 'Comida suave y fácil de digerir',
    color: '#10B981',
    orden: 2,
    activo: true,
  },
  {
    id: 'hipocalorico',
    nombre: 'Hipocalórico',
    descripcion: 'Opción baja en calorías',
    color: '#F59E0B',
    orden: 3,
    activo: true,
  },
  {
    id: 'sin-gluten',
    nombre: 'Sin Gluten',
    descripcion: 'Libre de gluten para celíacos',
    color: '#8B5CF6',
    orden: 4,
    activo: true,
  },
  {
    id: 'sandwich',
    nombre: 'Sandwich',
    descripcion: 'Opciones de sandwiches',
    color: '#EF4444',
    orden: 5,
    activo: true,
  },
  {
    id: 'bebida',
    nombre: 'Bebida',
    descripcion: 'Bebidas y líquidos',
    color: '#06B6D4',
    orden: 6,
    activo: true,
  },
];

export interface Minuta {
  id: string;
  nombre: string;
  descripcion?: string;
  fechaInicio: Date;
  fechaFin: Date;
  estado: EstadoMinuta;
  tipoMinuta: TipoMinuta;
  platos: PlatoMinuta[]; // Cambio de 'comidas' a 'platos'
  costoTotal?: number;
  porcionesTotales?: number;
  totalRecetas?: number; // Total de recetas asignadas (para la lista)
  fechaCreacion: Date;
  fechaActualizacion: Date;
  creadoPor: string;
}

export interface PlatoMinuta {
  id: string;
  minutaId: string;
  tipoPlato: TipoPlato;
  recetas: RecetaMinuta[]; // Múltiples opciones de recetas para este tipo
  disponible: boolean;
  orden: number;
  notas?: string;
}

export interface RecetaMinuta {
  id: string;
  platoId: string; // Cambio de comidaId a platoId
  recetaId: string;
  receta: Receta;
  porcionesRequeridas: number;
  stockSuficiente: boolean;
  ingredientesFaltantes: string[]; // permite agregar aún sin stock
  costoEstimado?: number;
  porcentajeStock: number; // % de ingredientes disponibles
}

export interface IngredienteAlerta {
  ingredienteId: string;
  nombreIngrediente: string;
  stockActual: number;
  stockRequerido: number;
  deficit: number;
  recetasAfectadas: string[];
  prioridad: 'alta' | 'media' | 'baja';
}

export interface MetricasMinutas {
  porcentajeIngredientesDisponibles: number; // % ingredientes disponibles próximas minutas
  proximoDiaSinMinuta: Date | null; // Día más próximo sin minuta planificada
  pedidosProximos7Dias: {
    confirmados: number;
    pendientes: number;
    total: number;
    porcentajeConfirmacion: number;
  };
  alertasStock: IngredienteAlerta[];
  minutasActivas: number;
  costoPromedioSemanal: number;
  recetasMasUsadas: {
    recetaId: string;
    nombreReceta: string;
    vecesUsada: number;
  }[];
  // Nuevas métricas ampliadas
  distribucionTiposPlatos: {
    tipoPlato: string;
    cantidad: number;
    porcentaje: number;
  }[];
  tendenciaCostos: {
    semana: string;
    costo: number;
    variacion: number; // % respecto a semana anterior
  }[];
  eficienciaOperacional: {
    minutasCompletas: number; // minutas con todos los platos asignados
    minutasIncompletas: number;
    porcentajeCompletitud: number;
  };
  proximasAlertas: {
    tipo: 'stock' | 'vencimiento' | 'planificacion';
    mensaje: string;
    prioridad: 'alta' | 'media' | 'baja';
    fechaLimite: Date;
  }[];
}

// Enums y tipos
export type EstadoMinuta = 'borrador' | 'activa' | 'archivada';
export type TipoMinuta = 'rango' | 'especial';
export type EstadoPlato = 'disponible' | 'agotado' | 'suspendido';

// Constantes para filtros
export const ESTADOS_MINUTA: {
  value: EstadoMinuta;
  label: string;
  color: string;
}[] = [
  { value: 'borrador', label: 'Borrador', color: 'yellow' },
  { value: 'activa', label: 'Activa', color: 'green' },
  { value: 'archivada', label: 'Archivada', color: 'gray' },
];

export const TIPOS_MINUTA: {
  value: TipoMinuta;
  label: string;
  icon: string;
  color: string;
}[] = [
  { value: 'rango', label: 'Rango', icon: 'calendar-range', color: 'blue' },
  { value: 'especial', label: 'Especial', icon: 'star', color: 'yellow' },
];

// Interfaces para formularios
export interface NuevaMinutaFormData {
  nombre: string;
  descripcion: string;
  fechaInicio: string;
  fechaFin: string;
  tipoMinuta: TipoMinuta;
  tiposPlatosSeleccionados: string[]; // Cambio de tiposComidaSeleccionados
}

export interface FiltrosMinutas {
  estado?: EstadoMinuta;
  tipoMinuta?: TipoMinuta;
  fechaInicio?: Date;
  fechaFin?: Date;
  busqueda?: string;
  conStock?: boolean; // true: solo con stock, false: solo sin stock, undefined: todos
}

// Utilidades para fechas
export const obtenerFechasEnRango = (inicio: Date, fin: Date): Date[] => {
  const fechas: Date[] = [];
  const fechaActual = new Date(inicio);

  while (fechaActual <= fin) {
    fechas.push(new Date(fechaActual));
    fechaActual.setDate(fechaActual.getDate() + 1);
  }

  return fechas;
};

export const formatearFechaMinuta = (fecha: Date): string => {
  return fecha.toLocaleDateString('es-CL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

export const esFinDeSemana = (fecha: Date): boolean => {
  const dia = fecha.getDay();
  return dia === 0 || dia === 6; // domingo o sábado
};

// Funciones para generar nombres automáticos de minutas
export const crearFechaLocal = (fechaString: string): Date => {
  // Crear fecha local sin problemas de zona horaria
  const [año, mes, dia] = fechaString.split('-').map(Number);
  return new Date(año, mes - 1, dia); // mes - 1 porque Date usa índices 0-11 para meses
};

export const generarNombreMinuta = (
  tipoMinuta: TipoMinuta,
  fechaInicio: Date | string,
  fechaFin: Date | string
): string => {
  const formatearFechaCorta = (fecha: Date): string => {
    return fecha.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
    });
  };

  // Asegurar que tenemos objetos Date correctos
  const fechaInicioDate =
    typeof fechaInicio === 'string'
      ? crearFechaLocal(fechaInicio)
      : fechaInicio;
  const fechaFinDate =
    typeof fechaFin === 'string' ? crearFechaLocal(fechaFin) : fechaFin;

  if (tipoMinuta === 'rango') {
    const inicio = formatearFechaCorta(fechaInicioDate);
    const fin = formatearFechaCorta(fechaFinDate);
    return `Menú ${inicio} a ${fin}`;
  } else if (tipoMinuta === 'especial') {
    const fecha = formatearFechaCorta(fechaInicioDate);
    return `Menú especial día ${fecha}`;
  }

  return 'Menú sin nombre';
};
